/**
 * Shared salary formatting utility for consistent display across all Jobs components.
 * Handles all SalaryType values: hourly, daily, monthly, annual, negotiable, competitive.
 * Supports multi-currency conversion via the currency converter module.
 */
import type { Currency } from '@/utils/currencyConverter';
import { convertCurrency } from '@/utils/currencyConverter';

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
};

export const formatJobSalary = (
  min?: number | null,
  max?: number | null,
  type?: string | null,
  fallback: string = 'Competitive',
  currency: Currency = 'USD'
): string => {
  if (!min && !max) return fallback;

  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const formatNum = (n: number) => {
    const converted = currency === 'USD' ? n : convertCurrency(n, currency);
    if (converted >= 1000000) return `${symbol}${(converted / 1000000).toFixed(1)}M`;
    if (converted >= 1000) return `${symbol}${Math.round(converted / 1000)}K`;
    return `${symbol}${Math.round(converted)}`;
  };

  const suffix =
    type === 'hourly' ? '/hr' :
    type === 'daily' ? '/day' :
    type === 'monthly' ? '/mo' :
    type === 'annual' ? '/yr' :
    type === 'negotiable' || type === 'competitive' ? '' :
    '/yr';

  if (min && max) {
    return `${formatNum(min)} - ${formatNum(max)}${suffix}`;
  }
  return min
    ? `From ${formatNum(min)}${suffix}`
    : `Up to ${formatNum(max!)}${suffix}`;
};
